import React, { useState, useEffect, useCallback } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Checkbox,
    FormControlLabel,
    CircularProgress,
    Alert,
    Divider,
    TextField,
    Chip,
    Tabs,
    Tab,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SendIcon from "@mui/icons-material/Send";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Axios from "axios";
import { getAuthHeader } from "../../utils/authToken";
import { fmtNyDate } from "./prepDates";
import { subjectLabelMap } from "./prepCalendarEvents";
import { emailPreviewHtml, emailPreviewDoc } from "./reportEmailPreview";

// Manual progress-report generation. The scheduled weekly report derives its contents (all of the
// program's ETT exams; homework scoped only by date); this dialog lets an admin hand-pick the
// students, the tests — ETT exams AND library (v2) tests — and the homework rounds instead.
//
// Nothing here emails anyone or writes anything: Generate is a preflight that reports per-student
// readiness, and each row's Download renders that student's PDF on demand.

// Toggle one id in a Set held in state (Sets are mutated in place, so clone first or React
// won't see a new reference and skip the re-render).
const toggleIn = (set, id) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
};

// The small gray second line under every picker option: when the item was released and when
// it's due. fmtNyDate renders "—" for a missing date, so both slots always read cleanly.
const datesLine = (release, due) => `Released ${fmtNyDate(release)} · Due ${fmtNyDate(due)}`;

// A two-line option label: the title, then its dates in small gray text below it.
const optionLabel = (title, release, due) => (
    <>
        {title}
        <Typography
            component="span"
            variant="caption"
            sx={{ display: "block", color: "text.secondary", lineHeight: 1.3 }}
        >
            {datesLine(release, due)}
        </Typography>
    </>
);

// Every picker column reads the same way: alphabetically by title, then soonest due, then
// soonest release as tiebreakers — regardless of the manual `order` the management panels
// sort by. localeCompare with numeric:true so "Part 2" sorts before "Part 10" instead of
// lexicographically after it; a missing date sorts last (Infinity).
const byTitleDueRelease = (titleOf) => (a, b) => {
    const t = (v) => (v ? new Date(v).getTime() : Infinity);
    return (
        (titleOf(a) || "").localeCompare(titleOf(b) || "", undefined, { numeric: true }) ||
        t(a.due_date) - t(b.due_date) ||
        t(a.release_date) - t(b.release_date)
    );
};

// A section header with an all/none toggle. `items` are already the selectable ids.
const SectionHeader = ({ label, items, selected, onChange, disabled }) => {
    const allOn = items.length > 0 && items.every((id) => selected.has(id));
    return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="overline" color="text.secondary">
                {label} {selected.size > 0 ? `(${selected.size})` : ""}
            </Typography>
            <Button
                size="small"
                disabled={disabled || !items.length}
                onClick={() => onChange(allOn ? new Set() : new Set(items))}
            >
                {allOn ? "Clear" : "Select all"}
            </Button>
        </Box>
    );
};

const GeneratePrepReportsModal = ({ open, onClose, programUuid, programName }) => {
    const base = `${process.env.REACT_APP_URL}/api/v1/prep-programs/${programUuid}`;
    const auth = { headers: { Authorization: getAuthHeader() } };

    const [loading, setLoading] = useState(false);
    const [students, setStudents] = useState([]);
    const [exams, setExams] = useState([]);
    const [v2Tests, setV2Tests] = useState([]);
    const [rounds, setRounds] = useState([]);
    const [subjectLabels, setSubjectLabels] = useState({}); // prep_program_course_id -> subject name

    const [pickedStudents, setPickedStudents] = useState(new Set());
    const [pickedExams, setPickedExams] = useState(new Set());
    const [pickedV2, setPickedV2] = useState(new Set());
    const [pickedRounds, setPickedRounds] = useState(new Set());

    const [busy, setBusy] = useState(false);
    const [step, setStep] = useState("pick"); // pick → review → sent
    const [drafts, setDrafts] = useState([]); // one per student per audience, editable
    const [skipped, setSkipped] = useState([]);
    const [sendResult, setSendResult] = useState(null);
    const [confirmSend, setConfirmSend] = useState(false);
    const [previewing, setPreviewing] = useState(null); // student_id whose PDF is rendering
    const [activeStudent, setActiveStudent] = useState(null); // review tab
    const [preview, setPreview] = useState("email"); // right pane: email body or the attachment
    const [previewAudience, setPreviewAudience] = useState(null); // which copy the email pane shows
    const [pdfUrls, setPdfUrls] = useState({}); // student_id -> blob URL, rendered lazily
    const [error, setError] = useState("");

    const loadAll = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const [s, e, v, r, d] = await Promise.all([
                Axios.get(`${base}/students`, auth),
                Axios.get(`${base}/exams`, auth),
                Axios.get(`${base}/v2-tests`, auth),
                Axios.get(`${base}/homework-rounds`, auth),
                // Rounds carry only a prep_program_course_id; the detail call is what turns that
                // into a subject name, so two rounds released the same day are tellable apart.
                Axios.get(base, auth),
            ]);
            setStudents(s.data || []);
            setExams(e.data || []);
            setV2Tests(v.data || []);
            setRounds(r.data || []);
            setSubjectLabels(subjectLabelMap((d.data.courses || []).filter((c) => c.role === "SUBJECT")));
        } catch (err) {
            console.error("Load report options error:", err);
            setError("Failed to load this program's students, tests and rounds");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [programUuid]);

    useEffect(() => {
        if (!open) return;
        setPickedStudents(new Set()); setPickedExams(new Set());
        setPickedV2(new Set()); setPickedRounds(new Set());
        setStep("pick"); setDrafts([]); setSkipped([]); setSendResult(null);
        setConfirmSend(false); setError(""); setPreviewing(null);
        setActiveStudent(null); setPreview("email"); setPreviewAudience(null); dropPdfs();
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, loadAll]);

    // Blob URLs live until explicitly revoked, so every path that invalidates them has to say so
    // — otherwise reopening the dialog a few times leaks a PDF per student per visit.
    const dropPdfs = () => setPdfUrls((prev) => {
        Object.values(prev).forEach((u) => window.URL.revokeObjectURL(u));
        return {};
    });

    // Revoke whatever is still cached when the dialog unmounts.
    useEffect(() => () => dropPdfs(), []);

    // Any change to the selection throws away composed drafts AND their rendered PDFs — both
    // describe the OLD selection, and sending them would mail a body and attachment that don't
    // match what's now on screen.
    const repick = (setter) => (value) => {
        setter(value); setDrafts([]); setStep("pick"); setActiveStudent(null); dropPdfs();
    };

    const hasContent = pickedExams.size > 0 || pickedV2.size > 0 || pickedRounds.size > 0;
    const canCompose = pickedStudents.size > 0 && hasContent;

    // Rounds usually have no label of their own, and a program with two subjects releases two
    // rounds on the same day — so lead with the round's own label, falling back to its subject.
    // The release/due dates ride along in the gray line under every option (see optionLabel).
    const roundTitle = (r) => r.label || subjectLabels[r.prep_program_course_id] || "Round";

    // The selection payload, shared by the preflight and every per-student download so the two
    // can't disagree about what the report contains.
    const selectionBody = () => ({
        exam_ids: [...pickedExams],
        v2_test_ids: [...pickedV2],
        round_ids: [...pickedRounds],
    });

    // Compose builds the real emails (one per student per audience) for review. It runs a report
    // build plus up to two AI narratives per student, so it's the slow step — hence its own
    // "Composing…" state rather than a spinner on the send button.
    const compose = async () => {
        setBusy(true);
        setError("");
        try {
            const res = await Axios.post(
                `${base}/reports/compose`,
                { student_ids: [...pickedStudents], ...selectionBody() },
                auth
            );
            // Track include/exclude and edits alongside each draft, keyed by student+audience.
            const composed = (res.data.drafts || []).map((d) => ({ ...d, include: true }));
            setDrafts(composed);
            setSkipped(res.data.skipped || []);
            setActiveStudent(composed.length ? composed[0].student_id : null);
            setStep("review");
        } catch (err) {
            console.error("Compose prep reports error:", err);
            setError(err.response?.data?.error || "Failed to compose the reports");
        } finally {
            setBusy(false);
        }
    };

    const editDraft = (idx, patch) =>
        setDrafts((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));

    // Render the active student's attachment and cache the blob URL. Rendering is per-student and
    // costs a puppeteer run, so it happens lazily on tab activation rather than for the whole
    // batch up front — and only once per student, since the PDF can't change while the selection
    // is fixed (changing the selection clears the whole cache).
    const loadPdf = useCallback(async (studentId, name) => {
        if (!studentId || pdfUrls[studentId]) return;
        setPreviewing(studentId);
        try {
            const res = await Axios.post(
                `${base}/students/${studentId}/export-pdf`,
                { title: `${name || "report"}.pdf`, inline: true, ...selectionBody() },
                { ...auth, responseType: "blob" }
            );
            const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            setPdfUrls((prev) => {
                // Two renders raced for the same student — keep the first and revoke the loser,
                // rather than silently dropping a live object URL.
                if (prev[studentId]) { window.URL.revokeObjectURL(blobUrl); return prev; }
                return { ...prev, [studentId]: blobUrl };
            });
        } catch (err) {
            console.error("Preview prep report error:", err);
            setError(`Failed to render the report for ${name || "this student"}`);
        } finally {
            setPreviewing(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [base, pdfUrls, pickedExams, pickedV2, pickedRounds]);

    const included = drafts.filter((d) => d.include);
    const recipientCount = included.reduce((n, d) => n + (d.to?.length || 0), 0);

    // One tab per student, in compose order; each carries the flags the tab label needs so
    // excluded copies and warnings stay visible without opening every tab.
    const studentTabs = drafts.reduce((acc, d) => {
        let row = acc.find((s) => s.student_id === d.student_id);
        if (!row) { row = { student_id: d.student_id, name: d.name, copies: 0, included: 0, warnings: 0 }; acc.push(row); }
        row.copies += 1;
        if (d.include) row.included += 1;
        if (d.warning) row.warnings += 1;
        return acc;
    }, []);
    const activeDrafts = drafts
        .map((d, idx) => ({ d, idx }))
        .filter(({ d }) => d.student_id === activeStudent);
    // The copy shown in the email pane: whichever card is selected, else this student's first.
    const previewDraft =
        activeDrafts.find(({ d }) => d.audience === previewAudience)?.d || activeDrafts[0]?.d || null;

    // Render the attachment only once the Attachment tab is actually opened — it costs a puppeteer
    // run per student, and an admin who only proofreads the emails should never pay for it.
    useEffect(() => {
        if (step !== "review" || !activeStudent || preview !== "pdf") return;
        const row = studentTabs.find((s) => s.student_id === activeStudent);
        loadPdf(activeStudent, row?.name);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, activeStudent, preview]);

    const send = async (dryRun) => {
        setBusy(true);
        setError("");
        try {
            const res = await Axios.post(
                `${base}/reports/send`,
                {
                    student_ids: [...pickedStudents],
                    ...selectionBody(),
                    dry_run: dryRun,
                    // Only the editable fields travel — recipients are re-resolved server-side.
                    drafts: included.map((d) => ({
                        student_id: d.student_id, audience: d.audience,
                        subject: d.subject, narrative: d.narrative,
                    })),
                },
                auth
            );
            setSendResult({ ...res.data, dryRun });
            setStep("sent");
        } catch (err) {
            console.error("Send prep reports error:", err);
            setError(err.response?.data?.error || "Failed to send the reports");
        } finally {
            setBusy(false);
            setConfirmSend(false);
        }
    };

    // One row per option. FormControlLabel is inline-flex with a -11px left margin by default
    // (it hangs the checkbox's padding into the gutter) — inside a scroll container that margin
    // clips the first character off every label, so it's cancelled here. Keep display flex: any
    // other value breaks the row and drops the text under the checkbox.
    const checkbox = (key, checked, onClick, label, disabled) => (
        <FormControlLabel
            key={key}
            sx={{ display: "flex", alignItems: "flex-start", ml: 0, mr: 0, py: 0.25 }}
            control={<Checkbox size="small" sx={{ py: 0.25 }} checked={checked} onChange={onClick} disabled={disabled} />}
            label={<Typography variant="body2" sx={{ pt: 0.4 }}>{label}</Typography>}
        />
    );

    const scrollBox = { maxHeight: 190, overflowY: "auto", pr: 1, mt: 0.5 };

    // Every column lists by title, then due date, then release date — not the manual `order`
    // the management panels use. Clone before sorting so the state arrays stay untouched.
    const sortedExams = [...exams].sort(byTitleDueRelease((e) => e.exam_title));
    const sortedV2 = [...v2Tests].sort(byTitleDueRelease((t) => t.test_title || `Test ${t.v2_test_id}`));
    const sortedRounds = [...rounds].sort(byTitleDueRelease(roundTitle));

    // Wider on review: the emails and the attached report sit side by side there, and md crushes
    // both. The picker is comfortable at either width.
    return (
        <Dialog open={open} onClose={onClose} maxWidth={step === "review" ? "xl" : "md"} fullWidth>
            <DialogTitle>Generate progress reports</DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                    {step === "pick" && "Pick what goes in the reports. Nothing is sent until you review and confirm."}
                    {step === "review" && "Review each email before it goes out. The subject and summary are editable; the tables below them come from the data."}
                    {step === "sent" && "Done."}
                </Typography>

                {loading && step === "pick" && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
                )}

                {step === "pick" && !loading && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                        <Box>
                            <SectionHeader
                                label="Students"
                                items={students.map((s) => s.id)}
                                selected={pickedStudents}
                                onChange={repick(setPickedStudents)}
                                disabled={busy}
                            />
                            <Box sx={scrollBox}>
                                {students.length === 0 && <Typography variant="body2" color="text.secondary">No active students in this program.</Typography>}
                                {students.map((s) => checkbox(
                                    s.id,
                                    pickedStudents.has(s.id),
                                    () => repick(setPickedStudents)(toggleIn(pickedStudents, s.id)),
                                    `${s.first_name || ""} ${s.last_name || ""}`.trim() || s.email,
                                    busy
                                ))}
                            </Box>
                        </Box>

                        <Divider />

                        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                            <Box sx={{ flex: "1 1 240px", minWidth: 240 }}>
                                <SectionHeader
                                    label="Practice exams"
                                    items={exams.map((e) => e.id)}
                                    selected={pickedExams}
                                    onChange={repick(setPickedExams)}
                                    disabled={busy}
                                />
                                <Box sx={scrollBox}>
                                    {exams.length === 0 && <Typography variant="body2" color="text.secondary">None configured.</Typography>}
                                    {sortedExams.map((e) => checkbox(
                                        e.id,
                                        pickedExams.has(e.id),
                                        () => repick(setPickedExams)(toggleIn(pickedExams, e.id)),
                                        optionLabel(e.exam_title, e.release_date, e.due_date),
                                        busy
                                    ))}
                                </Box>
                            </Box>

                            <Box sx={{ flex: "1 1 240px", minWidth: 240 }}>
                                <SectionHeader
                                    label="Library tests"
                                    items={v2Tests.map((t) => t.id)}
                                    selected={pickedV2}
                                    onChange={repick(setPickedV2)}
                                    disabled={busy}
                                />
                                <Box sx={scrollBox}>
                                    {v2Tests.length === 0 && <Typography variant="body2" color="text.secondary">None configured.</Typography>}
                                    {sortedV2.map((t) => checkbox(
                                        t.id,
                                        pickedV2.has(t.id),
                                        () => repick(setPickedV2)(toggleIn(pickedV2, t.id)),
                                        optionLabel(t.test_title || `Test ${t.v2_test_id}`, t.release_date, t.due_date),
                                        busy
                                    ))}
                                </Box>
                            </Box>

                            <Box sx={{ flex: "1 1 240px", minWidth: 240 }}>
                                <SectionHeader
                                    label="Homework rounds"
                                    items={rounds.map((r) => r.id)}
                                    selected={pickedRounds}
                                    onChange={repick(setPickedRounds)}
                                    disabled={busy}
                                />
                                <Box sx={scrollBox}>
                                    {rounds.length === 0 && <Typography variant="body2" color="text.secondary">None yet.</Typography>}
                                    {sortedRounds.map((r) => checkbox(
                                        r.id,
                                        pickedRounds.has(r.id),
                                        () => repick(setPickedRounds)(toggleIn(pickedRounds, r.id)),
                                        optionLabel(roundTitle(r), r.release_date, r.due_date),
                                        busy
                                    ))}
                                </Box>
                            </Box>
                        </Box>

                    </Box>
                )}

                {step === "review" && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {skipped.length > 0 && (
                            <Alert severity="warning">
                                Skipped: {skipped.map((s) => `${s.name || s.student_id} (${s.reason})`).join(", ")}
                            </Alert>
                        )}
                        {drafts.length === 0 && (
                            <Alert severity="info">
                                Nothing to send — the report schedule mails nobody for these students.
                            </Alert>
                        )}

                        {studentTabs.length > 0 && (
                            <>
                                {/* Tab labels carry the per-student state (how many copies are in,
                                    whether anything is flagged) so tabbing away never hides it. */}
                                <Tabs
                                    value={activeStudent}
                                    onChange={(e, v) => setActiveStudent(v)}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    sx={{ borderBottom: "1px solid", borderColor: "divider", minHeight: 44 }}
                                >
                                    {studentTabs.map((s) => (
                                        <Tab
                                            key={s.student_id}
                                            value={s.student_id}
                                            sx={{ minHeight: 44, textTransform: "none", opacity: s.included ? 1 : 0.5 }}
                                            label={
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                                    <span>{s.name}</span>
                                                    <Chip
                                                        size="small"
                                                        label={`${s.included}/${s.copies}`}
                                                        color={s.included ? "primary" : "default"}
                                                        variant={s.included === s.copies ? "filled" : "outlined"}
                                                        sx={{ height: 18, ".MuiChip-label": { px: 0.75, fontSize: 11 } }}
                                                    />
                                                    {s.warnings > 0 && <WarningAmberIcon sx={{ fontSize: 15 }} color="warning" />}
                                                </Box>
                                            }
                                        />
                                    ))}
                                </Tabs>

                                <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
                                    {/* Emails left, attachment right. Wraps to stacked on a narrow
                                        viewport so neither column gets crushed. */}
                                    <Box sx={{ flex: "1 1 460px", minWidth: 380, display: "flex", flexDirection: "column", gap: 2 }}>
                                        {activeDrafts.map(({ d, idx }) => (
                                            <Box
                                                key={`${d.student_id}-${d.audience}`}
                                                onClick={() => setPreviewAudience(d.audience)}
                                                sx={{
                                                    border: "1px solid",
                                                    // The highlighted card is the one the email pane is showing.
                                                    borderColor: previewDraft?.audience === d.audience ? "primary.main" : "divider",
                                                    borderRadius: 1, p: 1.5, cursor: "pointer",
                                                }}
                                            >
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                                    <Checkbox
                                                        size="small" sx={{ p: 0.5 }}
                                                        checked={d.include}
                                                        onChange={() => editDraft(idx, { include: !d.include })}
                                                        disabled={busy}
                                                    />
                                                    <Chip
                                                        size="small"
                                                        label={d.audience === "parent" ? "Parent · 한국어" : "Student · EN"}
                                                        color={d.audience === "parent" ? "secondary" : "default"}
                                                    />
                                                    <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1, textAlign: "right" }}>
                                                        {d.to.join(", ")}
                                                    </Typography>
                                                </Box>

                                                {d.warning && (
                                                    <Typography variant="caption" color="warning.main" sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                                                        <WarningAmberIcon sx={{ fontSize: 14 }} /> {d.warning}
                                                    </Typography>
                                                )}

                                                <TextField
                                                    fullWidth size="small" label="Subject" sx={{ mb: 1.5 }}
                                                    value={d.subject}
                                                    onChange={(e) => editDraft(idx, { subject: e.target.value })}
                                                    disabled={busy || !d.include}
                                                />
                                                <TextField
                                                    fullWidth multiline minRows={5} size="small"
                                                    label={d.audience === "parent" ? "Summary (Korean)" : "Summary (English)"}
                                                    helperText="The written summary at the top of the email. The score and homework tables below it come from the data."
                                                    value={d.narrative}
                                                    onChange={(e) => editDraft(idx, { narrative: e.target.value })}
                                                    disabled={busy || !d.include}
                                                />
                                            </Box>
                                        ))}
                                    </Box>

                                    <Box sx={{ flex: "1 1 420px", minWidth: 360, position: "sticky", top: 0 }}>
                                        <Tabs
                                            value={preview}
                                            onChange={(e, v) => setPreview(v)}
                                            sx={{ minHeight: 36, mb: 0.5 }}
                                        >
                                            <Tab value="email" label="Email" sx={{ minHeight: 36, textTransform: "none" }} icon={<MailOutlineIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
                                            <Tab value="pdf" label="Attachment" sx={{ minHeight: 36, textTransform: "none" }} icon={<PictureAsPdfIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
                                        </Tabs>
                                        <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, height: 620, overflow: "hidden", bgcolor: "#fff" }}>
                                            {preview === "email" ? (
                                                // Sandboxed: the email's own styles (and the footer) render as a mail
                                                // client would show them, without leaking into the dashboard's CSS.
                                                <iframe
                                                    title="Email preview"
                                                    srcDoc={previewDraft ? emailPreviewDoc(emailPreviewHtml(previewDraft, previewDraft.narrative)) : ""}
                                                    sandbox=""
                                                    style={{ width: "100%", height: "100%", border: 0, background: "#fff" }}
                                                />
                                            ) : pdfUrls[activeStudent] ? (
                                                <iframe
                                                    title="Attached report"
                                                    src={pdfUrls[activeStudent]}
                                                    style={{ width: "100%", height: "100%", border: 0 }}
                                                />
                                            ) : (
                                                <Box sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1 }}>
                                                    {previewing === activeStudent
                                                        ? <><CircularProgress size={24} /><Typography variant="caption" color="text.secondary">Rendering the report…</Typography></>
                                                        : <Typography variant="caption" color="text.secondary">Report unavailable</Typography>}
                                                </Box>
                                            )}
                                        </Box>
                                        {preview === "email" && activeDrafts.length > 1 && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                                Showing the {previewDraft?.audience === "parent" ? "parent (한국어)" : "student (EN)"} copy — click a card on the left to switch.
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </>
                        )}
                    </Box>
                )}

                {step === "sent" && sendResult && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        <Alert severity={sendResult.failed?.length ? "warning" : "success"}>
                            {sendResult.dryRun
                                ? `Test send: ${sendResult.sent.length} email${sendResult.sent.length === 1 ? "" : "s"} sent to you only.`
                                : `Sent ${sendResult.sent.length} email${sendResult.sent.length === 1 ? "" : "s"}.`}
                        </Alert>
                        {sendResult.sent.map((s, i) => (
                            <Typography key={i} variant="body2">
                                <CheckCircleOutlineIcon fontSize="small" color="success" sx={{ verticalAlign: "middle", mr: 0.5 }} />
                                {s.name} · {s.audience} → {s.to.join(", ")}
                            </Typography>
                        ))}
                        {(sendResult.failed || []).map((f, i) => (
                            <Typography key={i} variant="body2" color="error">
                                <WarningAmberIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />
                                Student {f.student_id} · {f.audience}: {f.reason}
                            </Typography>
                        ))}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>Close</Button>
                {step === "review" && (
                    <>
                        <Button onClick={() => setStep("pick")} disabled={busy}>Back</Button>
                        <Button onClick={() => send(true)} disabled={busy || !included.length}>
                            Send to me only
                        </Button>
                    </>
                )}
                {step === "pick" && (
                    <Button
                        variant="contained"
                        onClick={compose}
                        disabled={!canCompose || busy || loading}
                        startIcon={busy ? <CircularProgress size={20} /> : null}
                    >
                        {busy ? "Composing…" : `Compose${pickedStudents.size ? ` (${pickedStudents.size})` : ""}`}
                    </Button>
                )}
                {step === "review" && (
                    <Button
                        variant="contained" color="primary"
                        onClick={() => setConfirmSend(true)}
                        disabled={busy || !included.length}
                        startIcon={busy ? <CircularProgress size={20} /> : <SendIcon />}
                    >
                        {busy ? "Sending…" : `Send (${included.length})`}
                    </Button>
                )}
            </DialogActions>

            {/* Sending mails real families and can't be taken back — spell out the blast radius. */}
            <Dialog open={confirmSend} onClose={() => setConfirmSend(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Send these reports?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        {included.length} email{included.length === 1 ? "" : "s"} to {recipientCount} recipient
                        {recipientCount === 1 ? "" : "s"}, with each student's report attached. This cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmSend(false)}>Cancel</Button>
                    <Button variant="contained" onClick={() => send(false)} disabled={busy}>Send</Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default GeneratePrepReportsModal;
